/**
 * Les feuilles tierces de l'énoncé web : la mise en page de KaTeX, posée tout de suite, et les polices
 * (KaTeX, PT Serif, Lexend), chargées à la demande. Une fois par document.
 *
 * Inlinées, les polices pesaient 1,7 Mo — KaTeX y portait ses copies woff et ttf, qu'aucun navigateur
 * que nous servons ne lit : le web n'en garde que le woff2, 0,55 Mo en tout —, et la compression ne les
 * réduit presque pas. Importées en tête des moteurs web, elles étaient dans le bundle principal — en
 * double, un exemplaire par moteur —, et chaque utilisateur web les retéléchargeait à chaque mise en
 * prod, pour un énoncé qu'il n'ouvrirait peut-être pas. Par `import()`, elles forment un fichier à part, chargé
 * au premier énoncé ou message affiché. Le temps qu'il arrive, les formules sont déjà mises en page
 * (la feuille de KaTeX, 19 Ko, reste dans le bundle) et s'écrivent dans les polices de repli.
 * Son nom porte le numéro de module que Metro lui donne : un déploiement qui ajoute des modules avant
 * lui le renomme, et il se recharge alors au premier énoncé qui suit.
 *
 * Les deux moteurs web passent par ici — ce `<RichText>` et le `MarkdownWithLatex` du legacy, avec
 * les bulles de dialogue qui le réutilisent : une seule balise de chaque, un seul chargement.
 */
// eslint-disable-next-line import/no-unresolved
import katexLayoutCss from "../../../../assets/vendor/katex-layout.raw"

import { injectStyleOnce } from "../../utils/injectStyleOnce"

const KATEX_LAYOUT_TAG_ID = "masteur-rich-text-katex"
const FONTS_TAG_ID = "masteur-rich-text-fonts"

type FontFacesModule = typeof import("./richTextFontFaces")

/** Le fichier à part des polices ; un test passe le sien (Jest n'exécute pas `import()`). */
const loadFontFaces = (): Promise<FontFacesModule> => import("./richTextFontFaces")

let loadingFonts: Promise<void> | null = null

/**
 * À appeler au montage d'un moteur, AVANT de poser sa propre feuille : la sienne vient après celle de
 * KaTeX dans le document, et l'emporte à spécificité égale.
 */
export function ensureRichTextVendorCss(load: () => Promise<FontFacesModule> = loadFontFaces): Promise<void> {
    if (typeof document === "undefined") return Promise.resolve()
    injectStyleOnce(KATEX_LAYOUT_TAG_ID, katexLayoutCss)
    if (loadingFonts != null) return loadingFonts
    loadingFonts = load()
        .then(({ RICH_TEXT_FONT_FACES_CSS }) => injectStyleOnce(FONTS_TAG_ID, RICH_TEXT_FONT_FACES_CSS))
        .catch(() => {
            // Le fichier n'est pas arrivé (réseau coupé, version remplacée) : les formules restent mises
            // en page dans les polices de repli, et le prochain énoncé retentera.
            loadingFonts = null
        })
    return loadingFonts
}
